// src/components/DashboardSetup.jsx

import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import userPreferencesService, { DEFAULT_DASHBOARD_PANELS } from '../services/userPreferencesService';
import { useTheme } from '../contexts/ThemeContext';

export default function DashboardSetup() {
  const [panels, setPanels] = useState(DEFAULT_DASHBOARD_PANELS);
  const [loading, setLoading] = useState(true);
  const [, setSaving] = useState(false);
  const [, setLastSaved] = useState(null);
  const [mapType, setMapType] = useState('satellite');
  const [mapTypeLoading, setMapTypeLoading] = useState(true);
  const { theme } = useTheme();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const loadLayout = async () => {
      try {
        const layout = await userPreferencesService.loadDashboardLayout();
        setPanels(layout);
      } catch (error) {
        console.error('Failed to load dashboard layout:', error);
        setPanels(DEFAULT_DASHBOARD_PANELS);
      } finally {
        setLoading(false);
      }
    };

    const loadMapType = async () => {
      try {
        const type = await userPreferencesService.loadMapType();
        setMapType(type);
      } catch (error) {
        console.error('Failed to load map type:', error);
      } finally {
        setMapTypeLoading(false);
      }
    };

    loadLayout();
    loadMapType();
  }, []);

  const saveLayout = async (newPanels) => {
    setSaving(true);
    try {
      await userPreferencesService.saveDashboardLayout(newPanels);
      setLastSaved(new Date());
      setTimeout(() => setLastSaved(null), 3000);
    } catch (error) {
      console.error('Failed to save dashboard layout:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    setPanels(DEFAULT_DASHBOARD_PANELS);
    saveLayout(DEFAULT_DASHBOARD_PANELS);
  };

  const handleMapTypeChange = async (newMapType) => {
    setMapType(newMapType);
    try {
      await userPreferencesService.saveMapType(newMapType);
    } catch (error) {
      console.error('Failed to save map type:', error);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setPanels((items) => {
        const oldIndex = items.findIndex(item => item.key === active.id);
        const newIndex = items.findIndex(item => item.key === over.id);
        const newPanels = arrayMove(items, oldIndex, newIndex);
        
        // Auto-save on drag
        saveLayout(newPanels);
        
        return newPanels;
      });
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '16px', color: 'var(--cc-text-muted)' }}>Loading dashboard layout...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Map Type Preference */}
      <div style={{ marginBottom: '40px', paddingTop: '40px', borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}` }}>
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--branding-app-surface-panel-fg)' }}>
            Map View Preference
          </h3>
          <p style={{ margin: '0', fontSize: '14px', color: 'var(--branding-app-surface-panel-muted)', lineHeight: '1.5' }}>
            Choose your preferred map view for project locations. This will be applied to all project dashboards.
          </p>
        </div>

        {!mapTypeLoading && (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { value: 'satellite', label: 'Aerial view labels', description: 'Satellite imagery with street names and labels' },
              { value: 'roadmap', label: 'Street map', description: 'Standard road map view' }
            ].map((option) => {
              const active = mapType === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleMapTypeChange(option.value)}
                  style={{
                    background: active ? 'var(--branding-app-primary)' : (theme === 'dark' ? '#1a1a1a' : '#fff'),
                    color: active ? 'var(--branding-app-primary-fg)' : 'var(--branding-app-surface-panel-fg)',
                    border: `2px solid ${active ? 'var(--branding-app-primary)' : (theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0')}`,
                    borderRadius: '12px',
                    padding: '20px 28px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    minWidth: '220px',
                    flex: '1',
                    maxWidth: '320px',
                    boxShadow: active ? '0 4px 12px rgba(43, 53, 64, 0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    outline: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(255,255,255,0.25)' : '#cbd5e1';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '16px', userSelect: 'none', pointerEvents: 'none' }}>
                    {option.label}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.85, color: active ? 'var(--branding-app-primary-fg)' : 'var(--branding-app-surface-panel-muted)', lineHeight: '1.4', userSelect: 'none', pointerEvents: 'none' }}>
                    {option.description}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Dashboard Layout Header */}
      <div style={{ marginBottom: '24px', paddingTop: '40px', borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}` }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--branding-app-surface-panel-fg)' }}>
          Dashboard Layout
        </h3>
        <p style={{ margin: '0', fontSize: '14px', color: 'var(--branding-app-surface-panel-muted)', lineHeight: '1.5' }}>
          Drag and drop the panels below to reorder them. Your layout will be applied to all projects.
        </p>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
        <button
          onClick={resetToDefault}
          style={{
            background: 'var(--branding-app-surface)',
            color: 'var(--branding-app-surface-panel-sub)',
            border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = theme === 'dark' ? '#1f1f1f' : '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'var(--branding-app-surface)';
          }}
        >
          Reset to Default
        </button>
      </div>

      {/* Dashboard Grid */}
      <div style={{
        background: 'var(--branding-app-surface-panel)',
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`
      }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={panels.map(p => p.key)} strategy={rectSortingStrategy}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
              width: '100%',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {panels.map((panel) => (
                <SortablePanel key={panel.key} panel={panel} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

    </div>
  );
}

function SortablePanel({ panel }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: panel.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        ...style,
        background: 'var(--branding-app-surface)',
        borderRadius: '8px',
        padding: '20px',
        border: '1px solid var(--branding-app-surface-panel-border)',
        cursor: 'grab',
        userSelect: 'none',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'none',
        minHeight: '120px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--branding-app-surface-panel-fg)', marginBottom: '4px' }}>
        {panel.title}
      </div>
      <div style={{ fontSize: '12px', color: 'var(--branding-app-surface-panel-muted)', lineHeight: '1.3' }}>
        {panel.description}
      </div>
    </div>
  );
}
